import { SessionTimeoutDialog } from "@tlc/ui/dialog/TimeoutDialog";
import { createContext, useCallback, useEffect, useRef } from "react";
import React from "react";
import { useGlobalConfig } from "../components/GlobalConfig";
import { useAccount } from "../components/Account";
import { clearSearchHistory } from "../util/clearSensitiveSessionData";
import { useLocationConfiguration } from "../components/LocationConfiguration";
import { useNavigate } from "react-router-dom";
import { useDialog } from "@tlc/ui/dialog/useDialog";
import { useAnalytics } from "../analytics/useGoogleAnalytics";
import { useDialogRegistry } from "@tlc/ui/dialog/DialogRegistry";

export const TimeoutContext = createContext<{}>(null);

const WARNING_TIME = 60 * 2 * 1000; // 2 minutes before timeout
const SESSION_TIMEOUT_DEFAULT = 60 * 60 * 1000; // 60 minutes

const getTimeoutInMS = (timeout: number) => {
  if (!timeout) {
    return SESSION_TIMEOUT_DEFAULT;
  }
  return timeout * 60 * 1000;
};

export const TimeoutProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { closeAllOpenDialogs } = useDialogRegistry();
  const { trackLogout } = useAnalytics();
  const { globalSettings, adfsSettings } = useGlobalConfig();
  const {
    useRedirect,
    timeoutUrl,
    logoutUrl,
    enabled: adfsEnabled,
  } = adfsSettings || {};
  const { locationConfiguration } = useLocationConfiguration();
  const { pacSettings } = locationConfiguration || {};
  const { useAlternateTimeoutPage, alternateTimeoutPageUrl } =
    pacSettings || {};
  const { isAuthenticated } = useAccount();
  const isAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const timeTillTimeoutRef = useRef<number>(
    getTimeoutInMS(globalSettings.timeTillTimeout),
  );

  useEffect(() => {
    if (globalSettings.timeTillTimeout) {
      timeTillTimeoutRef.current = getTimeoutInMS(
        globalSettings.timeTillTimeout,
      );
    }
  }, [globalSettings.timeTillTimeout]);

  const warningTimeoutRef = useRef<number | null>(null);
  const sessionTimeoutRef = useRef<number | null>(null);
  const [expiration, setExpiration] = React.useState<Date | null>(null);

  const clearTimers = useCallback(() => {
    if (warningTimeoutRef.current) {
      window.clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (sessionTimeoutRef.current) {
      window.clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
    setExpiration(null);
  }, []);

  const { open, dialogProps, openDialog, closeDialog } = useDialog();

  const handleTimeout = useCallback(() => {
    clearSearchHistory();
    closeDialog();
    closeAllOpenDialogs();

    if (isAuthenticatedRef.current) {
      trackLogout(true);
    }
    // TODO trigger a close of all open dialogs...
    if (adfsEnabled) {
      if (useRedirect) {
        window.location.href = timeoutUrl; // TODO || logoutUrl?
      } else {
        // TODO window.location.href = logoutUrl?
        navigate("/"); // this make no sense to me...
      }
    } else if (useAlternateTimeoutPage && alternateTimeoutPageUrl) {
      // this also makes no sense... why aren't we logging the user out?
      // if this is the library or school home page, they may still be logged in when a user navigates to the pac
      window.location.href = alternateTimeoutPageUrl;
      // } else if (
      //   TLCCookie.get(LS2PAC_CONSTANTS.rememberMeCookieName) !== undefined
      // ) {
      //   // stay logged in but redirect to home page
      //   // not used since SPRING_SECURITY_REMEMBER_ME_COOKIE is set as HttpOnly
      //   navigate("/");
    } else {
      if (adfsEnabled) {
        // this block is actually unreachable... see TODO above
        window.location.href = logoutUrl;
      } else {
        if (isAuthenticatedRef.current) {
          navigate("/logout");
        } else {
          navigate("/");
        }
      }
    }
  }, [
    adfsEnabled,
    useRedirect,
    alternateTimeoutPageUrl,
    useAlternateTimeoutPage,
    timeoutUrl,
    logoutUrl,
    navigate,
    closeAllOpenDialogs,
    closeDialog,
    trackLogout,
  ]);

  const extendLocalWarningWindow = useCallback(() => {
    clearTimers();
    const expiration = new Date(Date.now() + timeTillTimeoutRef.current);
    setExpiration(expiration);
    sessionTimeoutRef.current = window.setTimeout(() => {
      handleTimeout();
    }, timeTillTimeoutRef.current);

    const timeTillWarning = Math.max(
      0,
      timeTillTimeoutRef.current - WARNING_TIME,
    );
    warningTimeoutRef.current = window.setTimeout(() => {
      // only display a warning if the user is authenticated
      if (isAuthenticatedRef.current) {
        openDialog();
      }
    }, timeTillWarning);
  }, [clearTimers, handleTimeout, openDialog]);

  useEffect(() => {
    if (open) {
      return; // suspend listeners while warning dialog is open
    }

    const resetTimer = () => {
      if (open) return;
      extendLocalWarningWindow();
    };

    extendLocalWarningWindow(); // initialize timers on mount

    const events: Array<keyof DocumentEventMap> = [
      "pointerdown",
      "keydown",
      "scroll",
      "touchstart",
    ];

    events.forEach((event) => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [extendLocalWarningWindow, open]);

  return (
    <TimeoutContext.Provider value={{}}>
      <SessionTimeoutDialog
        expiration={expiration}
        {...dialogProps}
        handleLogout={() => {
          closeDialog();
          closeAllOpenDialogs();
          trackLogout();
          navigate("/logout");
        }}
        handleSessionExtend={() => {
          closeDialog();
          extendLocalWarningWindow();
        }}
      />
      {children}
    </TimeoutContext.Provider>
  );
};
